import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { BrandName } from "@/components/BrandName";
import { useHomeNavigation } from "@/lib/useHomeNavigation";

const LINKS = [
  { label: "Services", hash: "#services" },
  { label: "Destinations", hash: "#destinations" },
  { label: "Process", hash: "#process" },
  { label: "Stories", hash: "#stories" },
  { label: "Contact", hash: "#contact" },
];

export default function Nav() {
  const { pathname } = useLocation();
  const scrollToSection = useHomeNavigation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => {
    setOpen(false);
    scrollToSection(hash);
  };

  return (
    <header
      data-testid="nav-main"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled || open ? "glass-card border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="container-x flex h-[72px] items-center justify-between">
        <BrandName placement="header" />
        <div className="hidden items-center gap-4 lg:flex xl:gap-7">
          {LINKS.map((l) => (
            <button
              key={l.hash}
              onClick={() => go(l.hash)}
              data-testid={`nav-link-${l.hash.slice(1)}`}
              className="group relative text-sm font-medium text-mist transition-colors duration-300 hover:text-frost"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-mint transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
          <Link to="/education-loan" aria-current={pathname === "/education-loan" ? "page" : undefined} data-testid="nav-link-education-loan" className="text-sm font-medium text-mintlight transition-colors hover:text-white">Education Loan</Link>
          <button
            onClick={() => go("#book")}
            data-testid="nav-book-counseling-btn"
            className="group flex items-center gap-2 rounded-full bg-mint px-5 py-2.5 text-sm font-bold text-ink transition-all duration-300 hover:bg-mintlight hover:shadow-[0_0_36px_rgba(74,222,128,0.35)]"
          >
            Book Free Counseling
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </button>
        </div>
        <button
          className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          aria-expanded={open}
          aria-controls="nav-mobile-menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {open && (
        <div id="nav-mobile-menu" className="glass-card border-t border-white/10 lg:hidden" data-testid="nav-mobile-menu">
          <div className="container-x flex flex-col py-4">
            {LINKS.map((l) => (
              <button
                key={l.hash}
                onClick={() => go(l.hash)}
                data-testid={`nav-mobile-link-${l.hash.slice(1)}`}
                className="border-b border-white/5 py-3.5 text-left text-base text-mist transition-colors hover:text-frost"
              >
                {l.label}
              </button>
            ))}
            <Link to="/education-loan" onClick={() => setOpen(false)} aria-current={pathname === "/education-loan" ? "page" : undefined} data-testid="nav-mobile-link-education-loan" className="border-b border-white/5 py-3.5 text-left text-base text-mintlight">Education Loan</Link>
            <button
              onClick={() => go("#book")}
              data-testid="nav-mobile-book-btn"
              className="mb-2 mt-4 rounded-full bg-mint px-5 py-3 text-sm font-bold text-ink"
            >
              Book Free Counseling
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
